/*
 * Copyright 2025 iodyne.  All rights reserved.
 * Use is subject to license terms.
 */

import { AuthStates, getAuthState } from "@/lib/auth/auth_state";
import { type ModelError } from "@/lib/models/errors";
import type { CloudTeamDetails, CloudTeamMembership, CloudTeamType } from "@/lib/models/people";
import { createEntityAdapter, type EntityState } from "@reduxjs/toolkit";
import { createApi, fetchBaseQuery } from "@reduxjs/toolkit/query/react";
import { translate as t } from "@/i18n/client_nohook";
import { responseForAPIError } from "@/lib/datastore/slices/helpers";
import { usersSlice } from "./users";

// Teams Storage
enum TeamAPIErrors {
  userDNEError = "UserDNEError",
  teamDNEError = "TeamDNEError",
  projectsReferenceTeamError = "ProjectsReferenceTeamError",
  usersDNEError = "UsersDNEError",
  tooManyMemberAddsError = "TooManyMemberAddsError",
  tooManyMemberDeletionsError = "TooManyMemberDeletionsError"
}

const teamsAdapter = createEntityAdapter({
  selectId: (e:CloudTeamType) => e.team_id
});
const teamsInitialState = teamsAdapter.getInitialState();

const teamMembershipAdapter = createEntityAdapter({
    selectId: (e:CloudTeamMembership) => e.team_id
});
const teamMembershipInitialState = teamMembershipAdapter.getInitialState();

export const teamsSlice = createApi({
    reducerPath: "teams_api",
    tagTypes: ["Team", "TeamMembership"],
    baseQuery: fetchBaseQuery({
        baseUrl: `${process.env.IOD_CLOUD_PORTAL_ACCOUNT_API_URI}/v2/ica/`,
        prepareHeaders: (headers, { getState }) => {
            headers.set("Authorization", `Bearer ${getAuthState(AuthStates.userAccessToken)}`)
            return headers;
        }
    }),
    endpoints: build => ({
        // List Teams
        listTeams: build.query<EntityState<CloudTeamType, string>, { org_id: string }>({
            //@ts-ignore
            providesTags: (result,_,arg) => result?.ids.map((id) => ({type: "Team", team_id:id})),
            query: ({ org_id }) => `org/${org_id}/teams`,
            transformResponse(resp: CloudTeamType[], _meta, { org_id }) {
                return teamsAdapter.setMany(teamsAdapter.getInitialState(), resp);
            },
            async transformErrorResponse(resp: { status: number, data: Record<string, any> }): Promise<ModelError> {
                return responseForAPIError(
                    resp,
                    {
                        [TeamAPIErrors.teamDNEError]: await t("user_mgmt.api_errors.team_dne")
                    }
                );
            }
        }),

        // Retrieve Team Detail
        getTeam: build.query<EntityState<CloudTeamType, string>, { team_id: string }>({
            providesTags: (result,_,team_id) => [{type: "Team", team_id:team_id}],
            query: ({ team_id }) => `team/${team_id}`,
            transformResponse(resp: CloudTeamType, _meta, { team_id }) {
                return teamsAdapter.setOne(teamsInitialState, resp);
            },
            async transformErrorResponse(resp: { status: number, data: Record<string, any> }): Promise<ModelError> {
                return responseForAPIError(
                    resp,
                    {
                        [TeamAPIErrors.teamDNEError]: await t("user_mgmt.api_errors.team_dne")
                    }
                );
            }
        }),

        // Create Team
        createTeam: build.mutation<EntityState<CloudTeamType, string>, { org_id: string, details: CloudTeamDetails }>({
            invalidatesTags: (result, error, team_id) => [
                { type: "Team", team_id: team_id }
            ],
            query: ({ org_id, details }) => ({
                url: `org/${org_id}/team`,
                method: "POST",
                body: details
            }),
            transformResponse(resp: CloudTeamType, meta, { details }) {
                return teamsAdapter.setOne(teamsInitialState, resp);
            }
        }),

        // Delete Team
        deleteTeam: build.mutation<EntityState<CloudTeamType, string>, { team_id: string }>({
            invalidatesTags: (result, error, team_id) => [
                { type: "Team", team_id: team_id }
            ],
            query: ({ team_id }) => ({
                url: `team/${team_id}`,
                method: "DELETE"
            }),
            onQueryStarted: ({team_id}, api) => {
                //Invalidate user's cache so team membership refreshes
                api.queryFulfilled.then(() => {
                    api.dispatch(usersSlice.util.invalidateTags([{type:"User"}]));
                });
            },
        }),

        // Update Team Detail
        updateTeam: build.mutation<EntityState<CloudTeamType, string>, { team_id: string, details: CloudTeamDetails }>({
            invalidatesTags: (result, error, team_id) => [
                { type: "Team", team_id: team_id }
            ],
            //@ts-ignore
            queryFn: async (arg, _queryApi, _extraOptions, baseQuery) => {
                var untyped_details = Object.assign({}, arg.details);

                const update_result = await baseQuery({
                    url: `team/${arg.team_id}`,
                    method: "PUT",
                    body: untyped_details
                });

                if (update_result.error) return { error: update_result.error };
                const team_detail: CloudTeamType = update_result.data as CloudTeamType;

                return { data: team_detail };
            },
            transformResponse(resp: CloudTeamType, meta, { details }) {
                return teamsAdapter.setOne(teamsInitialState, resp);
            },
            async transformErrorResponse(resp: { status: number, data: Record<string, any> }): Promise<ModelError> {
                return responseForAPIError(
                    resp,
                    {
                        [TeamAPIErrors.teamDNEError]: await t("team_mgmt.api_errors.team_dne")
                    }
                );
            }
        }),

        // Modify Team Membership
        modifyTeamMembership: build.mutation<EntityState<CloudTeamMembership, string>, { team_id: string, add_members: string[], del_members: string[] }>({
            invalidatesTags: (result, error, team_id) => [
                { type: "TeamMembership", team_id: team_id },
                { type: "Team", team_id: team_id }
            ],
            query: ({ team_id, add_members, del_members }) => ({
                url: `team/${team_id}/members`,
                method: "PUT",
                body: {
                    add_members: add_members,
                    del_members: del_members
                }
            }),
            onQueryStarted: ({add_members, del_members}, api) => {
                //Invalidate user's cache so team membership refreshes
                api.queryFulfilled.then(() => {
                    api.dispatch(usersSlice.util.invalidateTags([{type:"User"}]))
                });
            },
            transformResponse(resp: CloudTeamMembership, meta, { team_id }) {
                return teamMembershipAdapter.setOne(teamMembershipInitialState, resp);
            },
            async transformErrorResponse(resp: { status: number, data: Record<string, any> }): Promise<ModelError> {
                return responseForAPIError(
                    resp,
                    {
                        [TeamAPIErrors.teamDNEError]: await t("team_mgmt.api_errors.team_dne"),
                        [TeamAPIErrors.tooManyMemberAddsError]: await t("team_mgmt.api_errors.too_many_member_adds"),
                        [TeamAPIErrors.tooManyMemberDeletionsError]: await t("team_mgmt.api_errors.too_many_member_deletions")
                    }
                );
            }
        }),

        // Retrieve Team Membership
         getTeamMembership: build.query<EntityState<CloudTeamMembership, string>, { team_id: string }>({
            providesTags: (result,_,team_id) => [{type: "TeamMembership", team_id:team_id}],
            query: ({team_id}) => ({
                url: `team/${team_id}/members`,
                method: "GET"
            }),
            transformResponse(resp: CloudTeamMembership, meta, { team_id }) {
                return teamMembershipAdapter.setOne(teamMembershipInitialState, resp);
            }
         })
    })
});

export const {
    useGetTeamQuery,
    useListTeamsQuery,
    useUpdateTeamMutation,
    useDeleteTeamMutation,
    useCreateTeamMutation,
    useGetTeamMembershipQuery,
    useModifyTeamMembershipMutation
} = teamsSlice;

export const teamsSelectors = teamsAdapter.getSelectors();